// components/journey-surface/journey-svg-canvas.js
// ═══════════════════════════════════════════════════
// SVG canvas for the journey map — renders edges, nodes, and
// lens overlays (connections, ghosts). Also renders the file node
// hover tooltip.

import React from 'react';
import { NODE_TYPES, FILE_NODE_COLORS } from '../../lib/workspace-config';
import { NodeElement, EdgeElement } from './journey-nodes';

export function JourneySvgCanvas({
  positioned,
  edges,
  labels,
  activeLenses,
  focusedNodeId,
  hoveredFile,
  setHoveredFile,
  onNodeClick,
  viewWidth,
  viewHeight,
  mapOpen,
}) {
  return (
    <div style={{
      maxHeight: mapOpen ? 300 : 0,
      overflow: 'hidden',
      transition: 'max-height 0.3s ease',
      borderBottom: mapOpen ? '1px solid #1e1e2a' : 'none',
    }}>
      <div style={{ padding: 12, flexShrink: 0, position: 'relative' }}>
        <svg
          viewBox={`0 0 ${viewWidth} ${viewHeight}`}
          width="100%"
          style={{ display: 'block' }}
        >
          {/* Edges */}
          {edges.map((e, i) => (
            <EdgeElement key={`e-${i}`} from={e.from} to={e.to} />
          ))}
          {/* Cross-branch connection arcs (Connections lens) */}
          {activeLenses.has('connections') && positioned.length > 3 && (() => {
            // Find potential cross-branch connections: nodes that share
            // keywords in their titles but have different parents
            const arcs = [];
            for (let i = 0; i < positioned.length; i++) {
              for (let j = i + 1; j < positioned.length; j++) {
                const a = positioned[i], b = positioned[j];
                if (a.parent_id === b.parent_id) continue;
                const aWords = (a.title || '').toLowerCase().split(/\s+/).filter(w => w.length > 4);
                const bWords = (b.title || '').toLowerCase().split(/\s+/).filter(w => w.length > 4);
                if (aWords.some(w => bWords.includes(w))) {
                  arcs.push({ from: a, to: b });
                }
              }
            }
            return arcs.slice(0, 5).map((arc, i) => {
              const mx = (arc.from.x + arc.to.x) / 2;
              const my = Math.min(arc.from.y, arc.to.y) - 20;
              return (
                <path
                  key={`arc-${i}`}
                  d={`M ${arc.from.x} ${arc.from.y} Q ${mx} ${my} ${arc.to.x} ${arc.to.y}`}
                  stroke="rgba(155,143,196,0.3)"
                  strokeWidth={1}
                  strokeDasharray="3,3"
                  fill="none"
                />
              );
            });
          })()}
          {/* Nodes */}
          {positioned.map(n => (
            <NodeElement
              key={n.id}
              node={n}
              label={labels[n.id] || ''}
              onClick={onNodeClick}
              onHover={n.type === NODE_TYPES.FILE ? setHoveredFile : undefined}
              healthLensActive={activeLenses.has('health')}
              isFocused={n.id === focusedNodeId}
            />
          ))}
          {/* Ghost nodes (Ghosts lens) — predicted missing content */}
          {activeLenses.has('ghosts') && positioned.length > 1 && (() => {
            // Show 1-2 ghost nodes as children of the root
            const root = positioned.find(n => n.type === 'root');
            if (!root) return null;
            const ghostX = root.x + 45;
            const ghostY = root.y + 55;
            return (
              <g opacity={0.5}>
                <line x1={root.x} y1={root.y} x2={ghostX} y2={ghostY}
                  stroke="rgba(155,143,196,0.15)" strokeWidth={1} strokeDasharray="2,2" />
                <circle cx={ghostX} cy={ghostY} r={10}
                  fill="rgba(155,143,196,0.03)" stroke="rgba(155,143,196,0.4)"
                  strokeWidth={0.8} strokeDasharray="2,2" />
                <text x={ghostX} y={ghostY + 3} textAnchor="middle"
                  fontSize={7} fill="rgba(155,143,196,0.5)" fontFamily="Georgia, serif">
                  ?
                </text>
              </g>
            );
          })()}
          {/* Inline SVG legend */}
          <g transform={`translate(10, ${viewHeight - 16})`}>
            {[
              { label: 'Active', color: '#c8a96e' },
              { label: 'Building', color: '#c8a96e' },
              { label: 'Referenced', color: '#9b8fc4' },
              { label: 'Idle', color: '#555' },
              { label: 'Ghost', color: 'rgba(155,143,196,0.5)' },
            ].map((item, i) => (
              <g key={item.label} transform={`translate(${i * 50}, 0)`}>
                <circle cx={4} cy={4} r={3} fill={item.color} />
                <text x={10} y={7} fontSize={6} fill="#2e2e2e" fontFamily="Georgia, serif">{item.label}</text>
              </g>
            ))}
          </g>
        </svg>

        {/* File node hover preview — small tooltip pinned to the
            top-right of the SVG area. Showing the filename next
            to the node would require px-coordinate gymnastics
            because the SVG scales with the panel; pinning to a
            corner is honest about what we know. Pointer-events
            disabled so hover doesn't flicker. */}
        {hoveredFile && (
          <div
            style={{
              position: 'absolute',
              top: 18,
              right: 18,
              background: '#111',
              border: `1px solid ${(FILE_NODE_COLORS[hoveredFile.file_source] || FILE_NODE_COLORS.uploaded)}66`,
              borderRadius: 6,
              padding: '8px 12px',
              minWidth: 140,
              maxWidth: 220,
              pointerEvents: 'none',
              boxShadow: '0 4px 14px rgba(0,0,0,0.6)',
              zIndex: 20,
            }}
          >
            <div style={{
              fontSize: 9,
              color: FILE_NODE_COLORS[hoveredFile.file_source] || FILE_NODE_COLORS.uploaded,
              fontFamily: 'Georgia, serif',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              marginBottom: 4,
            }}>
              {labels[hoveredFile.id]} · {hoveredFile.file_source === 'generated' ? 'Shift' : 'Upload'}
            </div>
            <div style={{
              fontSize: 11,
              color: '#d4d4d4',
              fontFamily: 'Georgia, serif',
              lineHeight: 1.45,
              wordBreak: 'break-word',
            }}>
              {hoveredFile.title}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
